
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ErrorAlert } from "../dashboard/organization-editor/AlertDialogs";
import { 
  saveConversionRateToSupabase, 
  updateLateStageWinsInBridgeplan,
  loadConversionRate,
  fetchCountries,
  fetchAccountNames,
  fetchLeaderboardData
} from "./gap-analysis/supabaseService";
import { 
  calculateFunnelStages, 
  calculateMetrics,
  calculateRecommendations,
  getFilterLabel
} from "./gap-analysis/utils";
import FilterHeader from "./gap-analysis/FilterHeader";
import RecommendationsPanel from "./gap-analysis/RecommendationsPanel";
import RevenueSummary from "./gap-analysis/RevenueSummary";
import SalesFunnel from "./gap-analysis/SalesFunnel";
import DataTable from "./gap-analysis/DataTable";
import { GapAnalysisTabProps, LeaderboardData, FunnelStage } from "./gap-analysis/types";

// Constants
const regions = ["All", "North America", "Europe", "Asia Pacific", "Latin America", "Middle East & Africa"];
const productLines = ["All", "Enterprise Solutions", "Cloud Services", "Security Software", "Data Analytics", "AI & Machine Learning", "NA"];
const industries = ["All", "Telecom", "Technology", "FSI", "Manufacturing", "Public Sector", "Others"];

const GapAnalysisTab: React.FC<GapAnalysisTabProps> = ({ className }) => {
  // State variables
  const [selectedRegion, setSelectedRegion] = useState<string>("All");
  const [selectedCountry, setSelectedCountry] = useState<string>("All");
  const [countryOptions, setCountryOptions] = useState<string[]>(["All"]);
  const [selectedAccountName, setSelectedAccountName] = useState<string>("All");
  const [accountNameOptions, setAccountNameOptions] = useState<string[]>(["All"]);
  const [selectedProductLine, setSelectedProductLine] = useState<string>("All");
  const [selectedIndustry, setSelectedIndustry] = useState<string>("All");
  const [filterType, setFilterType] = useState<"product" | "industry">("product");
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [funnelStages, setFunnelStages] = useState<FunnelStage[]>([
    {
      name: "Prospecting stage",
      value: 0,
      color: "#8B5CF6",
      textColor: "#FFFFFF",
      widthClass: "w-full"
    }, 
    {
      name: "Qualification stage",
      value: 0,
      color: "#D946EF",
      textColor: "#FFFFFF",
      widthClass: "w-[calc(100%-40px)]"
    }, 
    {
      name: "Proposal stage",
      value: 0,
      color: "#F97316",
      textColor: "#FFFFFF",
      widthClass: "w-[calc(100%-80px)]"
    }, 
    {
      name: "Negotiation stage",
      value: 0,
      color: "#0EA5E9",
      textColor: "#FFFFFF",
      widthClass: "w-[calc(100%-120px)]"
    }
  ]);
  
  const [metrics, setMetrics] = useState({
    totalAchievement: 0,
    totalTarget: 0,
    totalRevenueGap: 0,
    revenueGapPercentage: "00.00",
    achievementPercentage: "00.00",
    totalLateStageValue: 0,
    avgRatio: "00.00",
    avgWinRatio: "00.00",
    lateStageToGapRatio: "00.00",
    potentialLateStageWinValue: 0
  });
  
  const [recommendations, setRecommendations] = useState({
    highestSurplus: [],
    highestWinRatio: [],
    lowestPerforming: [],
    totalLateStageValue: 0,
    lowestPerformingTotalValue: 0
  });
  
  const [isTableOpen, setIsTableOpen] = useState<boolean>(false);
  const [isPipelineTableOpen, setIsPipelineTableOpen] = useState<boolean>(false);
  const [conversionRate, setConversionRate] = useState<string>("");
  const [showValueAlert, setShowValueAlert] = useState<boolean>(false);
  const [valueToDisplay, setValueToDisplay] = useState<number>(0);

  // Load conversion rate on initial render
  useEffect(() => {
    const initialize = async () => {
      const rate = await loadConversionRate();
      setConversionRate(rate);
    };
    initialize();
  }, []);

  // Update metrics when data changes
  useEffect(() => {
    const updatedMetrics = calculateMetrics(leaderboardData, conversionRate);
    setMetrics(updatedMetrics);
    
    if (updatedMetrics.potentialLateStageWinValue > 0) {
      updateLateStageWinsInBridgeplan(updatedMetrics.potentialLateStageWinValue);
    }
    
    if (recommendations.totalLateStageValue > 0 && 
        parseFloat(metrics.avgWinRatio) > 0 && 
        parseFloat(conversionRate || "0") > 0) {
      const highPerformingValue = recommendations.totalLateStageValue * 
                                (parseFloat(metrics.avgWinRatio) / 100) * 
                                (parseFloat(conversionRate || "0") / 100);
      updateHighPerformingAccountsValue(highPerformingValue);
    }
    
    if (recommendations.lowestPerformingTotalValue > 0 && 
        parseFloat(metrics.avgWinRatio) > 0 && 
        parseFloat(conversionRate || "0") > 0) {
      const lowPerformersValue = recommendations.lowestPerformingTotalValue * 
                               (parseFloat(metrics.avgWinRatio) / 100) * 
                               (parseFloat(conversionRate || "0") / 100);
      updateLowPerformersRecoveryValue(lowPerformersValue);
    }
  }, [leaderboardData, conversionRate, recommendations.totalLateStageValue, recommendations.lowestPerformingTotalValue, metrics.avgWinRatio]);

  // Fetch countries when region changes
  useEffect(() => {
    const loadCountries = async () => {
      const countries = await fetchCountries(selectedRegion);
      setCountryOptions(countries);
      
      if (selectedCountry !== "All" && !countries.includes(selectedCountry)) {
        setSelectedCountry("All");
      }
    };
    loadCountries();
  }, [selectedRegion]);

  // Fetch account names when region or country changes
  useEffect(() => {
    const loadAccountNames = async () => {
      const accountNames = await fetchAccountNames(selectedRegion, selectedCountry);
      setAccountNameOptions(accountNames);
      
      if (selectedAccountName !== "All" && !accountNames.includes(selectedAccountName)) {
        setSelectedAccountName("All");
      }
    };
    loadAccountNames();
  }, [selectedRegion, selectedCountry]);

  // Fetch leaderboard data when filters change
  useEffect(() => {
    const loadLeaderboardData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const data = await fetchLeaderboardData(
          selectedRegion,
          selectedCountry,
          selectedAccountName,
          filterType,
          selectedProductLine,
          selectedIndustry
        );
        
        setLeaderboardData(data);
        
        const stagesData = calculateFunnelStages(data);
        setFunnelStages(stagesData);
        
        const calculatedMetrics = calculateMetrics(data, conversionRate);
        setMetrics(calculatedMetrics);
        
        const calculatedRecommendations = calculateRecommendations(data);
        setRecommendations(calculatedRecommendations);
      } catch (err) {
        console.error("Error fetching leaderboard data:", err);
        setError("Failed to fetch data. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };
    
    loadLeaderboardData();
  }, [selectedRegion, selectedCountry, selectedAccountName, selectedProductLine, selectedIndustry, filterType]);

  // Update metrics.potentialLateStageWinValue to bridgeplan table
  useEffect(() => {
    if (metrics.potentialLateStageWinValue > 0) {
      updateLateStageWinsInBridgeplan(metrics.potentialLateStageWinValue);
    }
  }, [metrics.potentialLateStageWinValue]);

  // Utility functions
  const updateHighPerformingAccountsValue = async (value: number) => {
    console.log("High-Performing Accounts value calculated:", value);
  };

  const updateLowPerformersRecoveryValue = async (value: number) => {
    console.log("Recovery of Low Performers value calculated:", value);
  };

  // Event handlers
  const handleConversionRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    
    if (value === '' || value === '.') {
      setConversionRate('');
    } else {
      const numericValue = parseFloat(value);
      
      if (!isNaN(numericValue) && numericValue >= 0 && numericValue <= 100) {
        const parts = value.split('.');
        
        if (parts.length > 2) {
          setConversionRate(`${parts[0]}.${parts[1]}`);
        } else if (parts.length === 2 && parts[1].length > 2) {
          setConversionRate(`${parts[0]}.${parts[1].substring(0, 2)}`);
        } else {
          setConversionRate(value);
          saveConversionRateToSupabase(value);
        }
      }
    }
  };

  const handleRegionChange = (value: string) => {
    setSelectedRegion(value);
    setSelectedCountry("All");
    setSelectedAccountName("All");
  };

  const handleCountryChange = (value: string) => {
    setSelectedCountry(value);
    setSelectedAccountName("All");
  };

  const handleAccountNameChange = (value: string) => {
    setSelectedAccountName(value);
  };

  const handleProductLineChange = (value: string) => {
    setSelectedProductLine(value);
  };

  const handleIndustryChange = (value: string) => {
    setSelectedIndustry(value);
  };

  const handleFilterTypeChange = (value: "product" | "industry") => {
    setFilterType(value);
  };

  // Computed values
  const filterLabel = getFilterLabel(
    selectedRegion, 
    selectedCountry, 
    selectedAccountName, 
    filterType, 
    selectedProductLine, 
    selectedIndustry
  );

  return (
    <div className={`text-white ${className || ''}`}>
      <FilterHeader 
        selectedRegion={selectedRegion}
        selectedCountry={selectedCountry}
        selectedAccountName={selectedAccountName}
        selectedProductLine={selectedProductLine}
        selectedIndustry={selectedIndustry}
        filterType={filterType}
        countryOptions={countryOptions}
        accountNameOptions={accountNameOptions}
        regions={regions}
        productLines={productLines}
        industries={industries}
        handleRegionChange={handleRegionChange}
        handleCountryChange={handleCountryChange}
        handleAccountNameChange={handleAccountNameChange}
        handleProductLineChange={handleProductLineChange}
        handleIndustryChange={handleIndustryChange}
        handleFilterTypeChange={handleFilterTypeChange}
      />
      
      <div className="p-4 border border-primary/20 px-[12px] rounded bg-gray-800 py-[9px]">
        {isLoading ? (
          <div className="flex justify-center items-center h-40">
            <p>Loading data...</p>
          </div>
        ) : error ? (
          <div className="flex justify-center items-center h-40">
            <p className="text-red-500">{error}</p>
            <Button variant="outline" onClick={() => window.location.reload()} className="ml-4">
              Retry
            </Button>
          </div>
        ) : (
          <>
            <div className="flex flex-row gap-4 mb-2">
              <RecommendationsPanel 
                recommendations={recommendations} 
                filterLabel={filterLabel} 
              />
              
              <RevenueSummary 
                metrics={metrics} 
                filterLabel={filterLabel} 
              />
              
              <SalesFunnel 
                funnelStages={funnelStages}
                metrics={metrics}
                conversionRate={conversionRate}
                handleConversionRateChange={handleConversionRateChange}
                filterLabel={filterLabel}
              />
            </div>

            <div className="mt-4">
              <DataTable 
                isOpen={isPipelineTableOpen}
                setIsOpen={setIsPipelineTableOpen}
                leaderboardData={leaderboardData}
                title="Pipeline Stages Detailed Data"
              />
            </div>
          </>
        )}
      </div>
      
      <ErrorAlert 
        open={showValueAlert} 
        onOpenChange={setShowValueAlert} 
        title="Potential Late Stage Win Revenue" 
        description={`The value being updated is: $${valueToDisplay.toLocaleString()}`} 
      />
    </div>
  );
};

export default GapAnalysisTab;
